import React from "react";
import { Button, Icons, Modal, Space } from "@pankod/refine-antd";

import { AuditActionTag } from "./audit-action-tag";
import { AuditMethodTag } from "./audit-method-tag";
import { AuditResultTag } from "./audit-result-tag";
import { AUDIT_LOG_TEXTS } from "../utils/audit-log.constants";
import {
  formatAuditDateTime,
  formatAuditPayload,
  getResourceLabel,
  getSafeText,
} from "../utils/audit-log.helpers";
import { AuditEvent } from "../types";

interface AuditLogDetailModalProps {
  event: AuditEvent | null;
  resourceLabels?: Record<string, string>;
  visible: boolean;
  onClose: () => void;
}

const DetailRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="audit-log-detail-row">
    <div className="audit-log-detail-label">{label}</div>
    <div className="audit-log-detail-value">{children}</div>
  </div>
);

export const AuditLogDetailModal: React.FC<AuditLogDetailModalProps> = ({
  event,
  resourceLabels = {},
  visible,
  onClose,
}) => {
  if (!visible || !event) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      width={760}
      footer={null}
      onCancel={onClose}
      wrapClassName="audit-log-detail-modal"
      title={
        <Space align="center" size={12}>
          <span className="audit-log-detail-title-icon">
            <Icons.FileTextOutlined />
          </span>
          <span>
            <span className="audit-log-detail-title">
              {AUDIT_LOG_TEXTS.detailsTitle}
            </span>
            <span className="audit-log-detail-subtitle">
              {formatAuditDateTime(event.at)}
            </span>
          </span>
        </Space>
      }
    >
      <div className="audit-log-detail-grid">
        <DetailRow label="Ator">
          <span>{getSafeText(event.actor_name || event.actor_id)}</span>
          {event.actor_name && event.actor_id && (
            <span className="audit-log-detail-code"> {event.actor_id}</span>
          )}
        </DetailRow>
        <DetailRow label="Ação">
          <Space size={8}>
            <AuditActionTag action={event.action || event.method} />
            <span className="audit-log-detail-code">{getSafeText(event.action)}</span>
          </Space>
        </DetailRow>
        <DetailRow label="Recurso">
          <Space size={8}>
            <span>{getResourceLabel(event.resource, resourceLabels)}</span>
            {event.resource && (
              <span className="audit-log-detail-code">{event.resource}</span>
            )}
          </Space>
        </DetailRow>
        <DetailRow label="Entidade">
          <span className="audit-log-detail-code">
            {getSafeText(event.entity_id)}
          </span>
        </DetailRow>
        <DetailRow label="Requisição">
          <Space size={8}>
            <AuditMethodTag method={event.method} />
            <span className="audit-log-detail-code">{getSafeText(event.path)}</span>
          </Space>
        </DetailRow>
        <DetailRow label="Resultado">
          <AuditResultTag outcome={event.outcome} status={event.status} />
        </DetailRow>
        <DetailRow label="IP de origem">
          <span className="audit-log-detail-code">{getSafeText(event.ip)}</span>
        </DetailRow>
        <DetailRow label="Request ID">
          <span className="audit-log-detail-code">
            {getSafeText(event.request_id)}
          </span>
        </DetailRow>
      </div>

      <div className="audit-log-payload-title">{AUDIT_LOG_TEXTS.payloadLabel}</div>
      <pre className="audit-log-payload-block">
        {formatAuditPayload(event.payload)}
      </pre>

      <div className="audit-log-detail-actions">
        <Button type="primary" onClick={onClose}>
          {AUDIT_LOG_TEXTS.closeButton}
        </Button>
      </div>
    </Modal>
  );
};
